import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import InlineEdit from './InlineEdit';
import { CheckCircleOutlined, DeleteOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons';
import { message, Popconfirm, Popover } from 'antd';
import { nullSafeGet, nullSafeGetOrElse, stopDefaultEvents } from '../../utils/DataAccessUtils';
import moment from 'moment';
import WOTaskAdditionalDetails from '../work_order_tasks/WOTaskAdditionalDetails';
import { connect } from 'react-redux';

const EachWOTask: FC<any> = ({
	task,
	onEnter,
	onSuccess,
	onDeleteSuccess,
	canEdit,
	onShowAssociate,
	workOrder,

	currentUser,
	onTaskEdit,
	onTaskDelete,
	timezonId,
	notCheckable,
}): React.ReactElement => {
	const [editMode, setEditMode] = useState(false);
	const [saving, setSaving] = useState(false);

	useEffect(() => {
		setEditMode(!nullSafeGetOrElse('title', task, '').trim());
	}, [task]);

	const onFinish = useCallback(
		(title) => {
			if (!(title || '').trim()) return;

			const entity = {
				...task,
				questionType: 'checkbox',
				title,
				...(!task.createdAt && { createdAt: moment.utc() }),
			};
			setSaving(true);
			onTaskEdit(entity)
				.then(onSuccess)
				.catch(message.error)
				.finally(() => setSaving(false));
		},
		[onSuccess, onTaskEdit, task]
	);

	const isChecked = useMemo(() => nullSafeGetOrElse('answer', task, 'false') === 'true', [task]);

	const canCheck = useMemo(
		() => (isChecked ? task.answeredBy === currentUser.email : true),
		[currentUser.email, isChecked, task.answeredBy]
	);

	const walkthroughExists = useMemo(() => !!nullSafeGet('walkthrough', task), [task]);

	const walkthrouNoteComplatedMessage = useMemo(
		() =>
			walkthroughExists
				? nullSafeGet('walkthrough.status', task) !== 'complete'
					? 'Walkthrough needs to be completed!'
					: ''
				: '',
		[task, walkthroughExists]
	);

	const onCheck = useCallback(() => {
		if (!nullSafeGetOrElse('title', task, '').trim() || !canCheck) {
			return;
		}

		if (!isChecked && walkthrouNoteComplatedMessage) {
			message.error(walkthrouNoteComplatedMessage);
			return;
		}

		const answer = nullSafeGetOrElse('answer', task, 'false') === 'false';
		setSaving(true);
		onTaskEdit({
			...task,
			answer: `${answer}`,
			...(!!answer && { answeredBy: currentUser.email }),
			...(!!answer && { answeredAt: moment.utc() }),
		})
			.then(onSuccess)
			.catch(message.error)
			.finally(() => setSaving(false));
	}, [
		canCheck,
		currentUser.email,
		isChecked,
		onSuccess,
		onTaskEdit,
		task,
		walkthrouNoteComplatedMessage,
	]);

	const onDeleteTask = useCallback(
		(e) => {
			if (saving) return;
			stopDefaultEvents(e);

			setSaving(true);
			onTaskDelete(task)
				.then(onDeleteSuccess)
				.catch(message.error)
				.finally(() => setSaving(false));
		},
		[onDeleteSuccess, onTaskDelete, saving, task]
	);

	const onItemClick = useCallback(() => {
		canEdit && !saving && setEditMode(true);
	}, [canEdit, saving]);

	const _showAssociateModal = useCallback(() => {
		if (walkthroughExists) {
			message.error('Walkthrough already scheduled!');
			return;
		}

		onShowAssociate(task);
	}, [onShowAssociate, task, walkthroughExists]);

	return (
		<div className="group my-2 flex flex-row items-center justify-between">
			<div className="mr-2 flex w-full flex-col items-start justify-start">
				<div className={`mr-2 flex flex-row items-center ${isChecked ? '' : 'w-full'}`}>
					{notCheckable ? (
						<div>-</div>
					) : isChecked ? (
						<Popover
							trigger="hover"
							content={canCheck ? '' : 'You are not allowed to uncheck this task'}
						>
							<div className="mr-2">
								<CheckCircleOutlined
									className="text-l m-1 hover:cursor-pointer hover:text-[#FFFFF] "
									onClick={onCheck}
								/>
							</div>
						</Popover>
					) : (
						<div className="mr-2 flex flex-row items-center justify-center" onClick={onCheck}>
							<div
								style={{
									border: 'solid 1px rgba(0, 0, 0, 0.75)',
									borderRadius: '50%',
								}}
								className="m-1 h-4 w-4 hover:cursor-pointer"
							/>
						</div>
					)}
					{editMode && canEdit && !isChecked ? (
						<InlineEdit
							placeholder="Enter task title"
							value={task.title}
							setValue={onFinish}
							onEnter={onEnter}
						/>
					) : (
						<div
							className={`flex flex-row pl-2 ${canEdit ? 'hover:cursor-pointer' : ''} ${
								isChecked ? 'line-through' : ''
							}`}
							onClick={onItemClick}
						>
							{nullSafeGetOrElse('title', task, '')}
						</div>
					)}
					<div
						className={`ml-4 flex hidden flex-row items-center ${
							canEdit ? 'hover:cursor-pointer group-hover:flex' : ''
						}`}
					>
						{!editMode ? (
							<>
								<div className="mr-4">
									<EditOutlined onClick={onItemClick} />
								</div>

								<div className="mr-4">
									<PlusOutlined onClick={_showAssociateModal} />
								</div>
							</>
						) : null}
						{task.id ? (
							<Popconfirm
								onConfirm={onDeleteTask}
								placement="topLeft"
								title="Are you sure you want to delete?"
								okText="Yes"
								cancelText="No"
							>
								<DeleteOutlined />
							</Popconfirm>
						) : (
							<DeleteOutlined onClick={onDeleteTask} />
						)}
					</div>
				</div>
				<div className="ml-4">
					<WOTaskAdditionalDetails
						task={task}
						timeZone={timezonId}
						onSave={onTaskEdit}
						workOrder={workOrder}
					/>
				</div>
			</div>
			<div className={`h-4 w-4 ${canEdit ? 'group-hover:hidden' : ''}`} />
		</div>
	);
};

const mapStateToProps = (state) => ({
	currentUser: state.session.currentUser,
});

const ComponentWithoutUserType = connect(mapStateToProps)(EachWOTask);

export default connect(
	(state) => ({
		userType: (state as any).session.userType,
	}),
	() => ({})
)(ComponentWithoutUserType);
